import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { noticeStore } from "./noticeStore.js";
import { NoticeItem } from "./NoticeItem.jsx";
import { formatDecoupledError } from "./decoupledError.js";
import "./notice.css";

const CONTAINER_ID = "orbit-notice-root";

// Module-scope guard so multiple <NoticeHost /> instances (e.g. mounted in
// both OrbitShell and PublicShell during route transitions) share a single
// portal container.
let mountCount = 0;

const subscribe = (listener) => noticeStore.subscribe(listener);
const getSnapshot = () => noticeStore.getAll();
const getServerSnapshot = () => [];

/**
 * Listener for DataSync CustomEvents. They bubble from the hidden <span> inside
 * any DataSync up to document. We auto-display errors and ignore successes
 * (no message context for a generic success notice — opt-in via the YAML
 * `notice` reaction on the consumer side, or props on DataSync via a custom
 * notice config attached to event.detail.noticeConfig).
 *
 * The message is built by formatDecoupledError, which surfaces the per-field
 * validation detail (decoupled_toolbox `errors` / `errors_labeled`) instead of
 * the generic "Validation failed." umbrella string.
 */
const handleSyncError = (event) => {
    const detail = event?.detail || {};
    if (detail.noticeConfig) {
        noticeStore.add(detail.noticeConfig);
        return;
    }
    const status = detail.responseContext?.status;
    const { message } = formatDecoupledError(detail.value, status);
    noticeStore.add({ type: "error", message });
};

const handleSyncSuccess = (event) => {
    const detail = event?.detail || {};
    if (detail.noticeConfig) {
        noticeStore.add(detail.noticeConfig);
    }
};

/**
 * Renders the notice stack in a portal attached to document.body. Mount once
 * (typically at shell root). Listens to bubbled `syncSuccess` / `syncError`
 * events from reactive-json's DataSync.
 */
export const NoticeHost = () => {
    const [container, setContainer] = useState(null);
    const notices = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

    useEffect(() => {
        if (typeof document === "undefined") return undefined;

        mountCount += 1;
        const isOwner = mountCount === 1;

        if (!isOwner) {
            return () => {
                mountCount -= 1;
            };
        }

        let el = document.getElementById(CONTAINER_ID);
        let createdHere = false;
        if (!el) {
            el = document.createElement("div");
            el.id = CONTAINER_ID;
            document.body.appendChild(el);
            createdHere = true;
        }
        setContainer(el);

        document.addEventListener("syncSuccess", handleSyncSuccess);
        document.addEventListener("syncError", handleSyncError);

        return () => {
            mountCount -= 1;
            document.removeEventListener("syncSuccess", handleSyncSuccess);
            document.removeEventListener("syncError", handleSyncError);
            if (createdHere && el.parentNode) {
                el.parentNode.removeChild(el);
            }
            setContainer(null);
        };
    }, []);

    // La hauteur de la pile, publiée pour que la barre du coin (« Signaler »)
    // se pose AU-DESSUS d'elle : les deux vivent désormais dans le même coin,
    // à gauche d'un panneau ouvert. Mesurée plutôt que devinée — une pile de
    // trois messages longs ne fait pas la hauteur d'un seul.
    const pileRef = useRef(null);
    useLayoutEffect(() => {
        const poser = (px) => document.documentElement.style.setProperty("--orbit-notices-hauteur", `${px}px`);
        const el = pileRef.current;
        if (!el) { poser(0); return undefined; }
        // `+ 12` : la gouttière entre la pile et la barre, sinon elles se touchent.
        const mesurer = () => poser(el.offsetHeight ? el.offsetHeight + 12 : 0);
        mesurer();
        const observer = new ResizeObserver(mesurer);
        observer.observe(el);
        return () => { observer.disconnect(); poser(0); };
    }, [notices.length]);

    if (!container) return null;

    return createPortal(
        <div
            ref={pileRef}
            // Deux décalages, deux raisons.
            //
            // `right` : un panneau latéral droit ouvert (l'assistant du builder)
            // occupe ce coin. Les notifications s'affichaient PAR-DESSUS son
            // composeur — on ne pouvait plus écrire tant qu'un message était là.
            // La même variable que la barre du coin les range à sa GAUCHE.
            //
            // `bottom` : c'est cette barre-là qui vit dans le coin dégagé
            // (« Signaler »). La pile se pose au-dessus d'elle plutôt que
            // dessus, en lisant la hauteur qu'elle publie.
            style={{
                right: "calc(1rem + var(--orbit-dock-inset, 0px))",
                transition: "right 300ms ease-in-out",
            }}
            className="pointer-events-none fixed bottom-4 z-[9999] flex flex-col-reverse gap-2"
            role="region"
            aria-label="Notifications"
        >
            {notices.map((notice) => (
                <NoticeItem
                    key={notice.id}
                    id={notice.id}
                    type={notice.type}
                    message={notice.message}
                    duration={notice.duration}
                    onClose={(id) => noticeStore.remove(id)}
                />
            ))}
        </div>,
        container
    );
};
